import React from 'react';
import './styles/About.css';
import test from './../assets/testWoodsBG3.webp';

import { Link } from 'react-router-dom';
import config from '../config/config';
import SEO from '../seo/SEO';
import generateBreadcrumbs from '../utility/generateBreadcrumbs';

const About = () => {

    const aboutJsonLd = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Withrow Knives",
        "url": `${config.metadata.siteUrl}/about`,
        "logo": `${config.metadata.siteUrl}/logo512.png`,
        "description": "Withrow Knives is dedicated to crafting custom handmade knives and leather sheaths. Meet our expert knife makers, Jason and Larry Withrow.",
        "breadcrumb": generateBreadcrumbs(["about"]),
        "founder": [
          {
            "@type": "Person",
            "name": "Jason Withrow",
            "jobTitle": "Co-Founder & Knife Maker",
            "description": "Jason Withrow, a dedicated knife maker specializing in custom fixed-blade and folding knives. He is a Project Management Professional and a craftsman passionate about high-quality blades."
          },
          {
            "@type": "Person",
            "name": "Larry Withrow",
            "jobTitle": "Co-Founder & Knife Maker",
            "description": "Larry Withrow, a Vietnam Veteran and expert in Damascus and San Mai steel knife-making. With over two decades of experience, he is a master of handcrafted knives and leather sheaths."
          }
        ],
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${config.metadata.siteUrl}/about`
        }
      };


    return (
        <>

            <SEO
                title="About Withrow Knives - Craftsmanship, Tradition, and Passion"
                description="Learn about Withrow Knives, a brand built on craftsmanship and passion. Meet Jason and Larry Withrow, expert knife makers dedicated to precision and quality."
                keywords={[
                "custom knives",
                "handmade knives",
                "knife makers",
                "Withrow Knives",
                "fixed blade knives",
                "folding knives",
                "damascus knives",
                "custom leather sheaths"
                ]}
                url={`${config.metadata.siteUrl}/about`}
                jsonLd={aboutJsonLd}
            />



            <div className='page-wrapper'>
                {/* ✅ Page Title with Background */}
                <div 
                    className='page-title' 
                    style={{ backgroundImage: `url(${test})`, backgroundSize: "cover", backgroundPosition: "center" }}
                >
                    <h1>ABOUT US</h1>
                </div>

                {/* ✅ Main About Content Section */}
                <section className='page-content-section'>
                    <div className="about-text">
                        <p>
                            Withrow Knives is dedicated to the art of handcrafted blades, blending tradition with innovation.
                            Founded by passionate makers, our knives are forged with precision, skill, and a love for the craft.
                        </p>
                        <p>
                            Every blade tells a story—whether it’s a rugged <strong>fixed-blade</strong>, a sleek <strong>folding knife</strong>, or a hand-forged <strong>damascus masterpiece</strong>. 
                            Each knife is a product of years of experience and an unwavering commitment to quality.
                        </p>
                    </div>

                    

                    {/* ✅ Meet the Makers Section */}
                    <div className="makers-section">
                        <h2>Meet the Makers</h2>

                                                {/* 🔹 Larry Withrow Bio */}
                                                <article className="maker-bio">
                            <h3>Larry Withrow</h3>
                            <p>
                                Born in 1945 in Charleston, WV, <strong>Larry Withrow</strong> has been a lifelong resident of the city. He is married with one son and is a proud <strong>Vietnam Veteran</strong>, 
                                having served <strong>four years in the United States Air Force</strong>. 
                            </p>
                            <p>
                                Over the years, Larry has enjoyed hobbies such as <strong>hunting, fishing, motorcycling, archery, whitewater rafting, kayaking, and martial arts</strong>. 
                                Nowadays, he focuses on <strong>bicycling and knife-making</strong>. 
                            </p>
                            <p>
                                Larry worked <strong>31 years for Carbide/Dow</strong> as a computer operator before retiring. He began making knives in <strong>2001</strong>, forging his own <strong>damascus, san mai, 
                                and 1095 steel blades</strong>, along with crafting <strong>liner-lock folders</strong>. He takes pride in forging his own materials and even <strong>cuts, stamps, and hand-sews </strong> 
                                his own knife sheaths. 
                            </p>
                            <p>
                                For more information on Larry’s knife-making, visit <a 
                                    href='https://chickensintheroad.com/classic/house/crafts/trip-to-a-knifemaker/' 
                                    target='_blank' 
                                    rel="noopener noreferrer nofollow"
                                    aria-label="Traveling West Virginia - Larry Withrow (Opens in a new tab)"
                                    >
                                    “Traveling West Virginia | Larry Withrow” <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
                                    </a>
                            </p>
                        </article>

                        {/* 🔹 Jason Withrow Bio */}
                        <article className="maker-bio">
                            <h3>Jason & Cindy Withrow</h3>
                            <p>
                                Born in 1980, Jason Withrow is the nephew of Larry Withrow. Originally from Sissonville, WV, he has lived in Eleanor, WV, since 2002 
                                with his wife and their four fur-baby kitties. Jason has a background in project management, earning his <strong>Master's Degree in 2011</strong> and 
                                becoming a <strong>certified Project Management Professional in 2013</strong>. 
                            </p>
                            <p>
                                He currently works as a <strong>Manager of Project Scheduling for TC Energy</strong> in Charleston, WV. His hobbies include <strong>motorcycling, UTV trail riding, 
                                and knife-making</strong>. Jason began dabbling in knife-making about <strong>10 years ago</strong>, but his passion grew stronger under the guidance of his uncle Larry. 
                                He specializes in <strong>fixed-blade stainless and carbon knives</strong>, and he has recently started crafting <strong>folders</strong>.
                            </p>
                            <p>
                                While he can make his own sheaths, Jason prefers his wife, <strong>Cindy</strong>, to handle that part of the craft. Cindy <strong>cuts, stamps, and hand-sews </strong> 
                                custom knife sheaths, adding an extra touch of craftsmanship to each blade.
                            </p>
                        </article>


                    </div>

                    {/* ✅ Process Summary */}
                    <div className="process-summary">
                        <h2>Our Knife-Making Process</h2>
                        <p>
                            From forging raw steel to crafting custom leather sheaths, each Withrow Knife is made with attention to detail at every step.
                            Discover how we bring our knives to life through our unique process.
                        </p>
                        <Link to="/our-process" className="read-more-link" aria-label="Learn more about our knife-making process">
                            Learn More About Our Process <i className="fa-solid fa-arrow-right"></i>
                        </Link>
                    </div>


                </section>
            </div>
        </>
    );
};

export default About;
